import { Bus, FileText, MapPin, Route as RouteIcon, Share2, UserCog } from 'lucide-react';

import { DonutChart, type DonutSlice } from '@/components/ui/DonutChart';
import { cn } from '@/lib/utils';
import type { RouteSummary } from '@/types/transport';
import { QuickActionGrid, RailCard, type QuickAction } from './TransportSideRail';

export interface RoutesSideRailProps {
  summary: RouteSummary;
  actions: QuickAction[];
  onViewRouteReport?: () => void;
  onViewDistanceReport?: () => void;
}

/**
 * The Routes tab's rail: how the routes divide, how far they run, and the six
 * things an office does to them.
 *
 * Its own component rather than a variant of the dashboard's rail, because it
 * answers different questions — that one is about today, this one is about the
 * register. They share `RailCard`, which is the part that would otherwise be
 * copied (CLAUDE.md §3).
 */
export function RoutesSideRail({
  summary,
  actions,
  onViewRouteReport,
  onViewDistanceReport,
}: RoutesSideRailProps) {
  const slices: DonutSlice[] = [
    {
      key: 'active',
      label: 'Active',
      value: summary.activeRoutes,
      stroke: 'stroke-emerald-500',
      dot: 'bg-emerald-500',
    },
    {
      key: 'inactive',
      label: 'Inactive',
      value: summary.inactiveRoutes,
      stroke: 'stroke-amber-500',
      dot: 'bg-amber-500',
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <RailCard
        title="Route overview"
        action={onViewRouteReport && { label: 'View report', onSelect: onViewRouteReport }}
      >
        <DonutChart
          slices={slices}
          total={summary.totalRoutes}
          caption="Total routes"
          layout="responsive"
        />
      </RailCard>

      <RailCard
        title="Distance overview"
        action={onViewDistanceReport && { label: 'View report', onSelect: onViewDistanceReport }}
      >
        <p className="text-2xl font-[800] leading-none tracking-tight text-ink">
          {summary.totalDistanceKm} km
        </p>
        <p className="mt-1 text-sm font-semibold text-ink-2">Total distance</p>

        <dl className="mt-4 grid grid-cols-2 gap-3 border-t border-line pt-3.5">
          <div className="min-w-0">
            <dt className="lbl text-ink-3">Average per route</dt>
            <dd className="mt-0.5 truncate text-sm font-bold text-ink">
              {summary.averageDistanceKm} km
            </dd>
          </div>
          <div className="min-w-0">
            <dt className="lbl text-ink-3">Longest route</dt>
            <dd className="mt-0.5 truncate text-sm font-bold text-ink">
              {summary.longestRouteKm} km
            </dd>
          </div>
        </dl>
      </RailCard>

      <RailCard title="Route status">
        <ul className="flex flex-col gap-1">
          <StatusLine label="Active routes" value={summary.activeRoutes} dot="bg-emerald-500" />
          <StatusLine label="Inactive routes" value={summary.inactiveRoutes} dot="bg-amber-500" />
        </ul>
      </RailCard>

      <RailCard title="Quick actions">
        <QuickActionGrid actions={actions} />
      </RailCard>
    </div>
  );
}

/** One "label … number" line, with a coloured mark matching the ring's slice. */
function StatusLine({ label, value, dot }: { label: string; value: number; dot: string }) {
  return (
    <li className="flex items-center justify-between gap-3 rounded-xl px-2 py-2 transition-colors hover:bg-surface-2">
      <span className="inline-flex min-w-0 items-center gap-2.5">
        <span className={cn('h-2 w-2 shrink-0 rounded-pill', dot)} aria-hidden />
        <span className="truncate text-sm font-semibold text-ink-2">{label}</span>
      </span>
      <span className="shrink-0 text-sm font-bold text-ink">{value}</span>
    </li>
  );
}

/** The six things the office does to a route. */
export const ROUTE_ACTION_ICONS = {
  addRoute: RouteIcon,
  assignVehicle: Bus,
  assignDriver: UserCog,
  manageStops: MapPin,
  routeReports: FileText,
  exportRoutes: Share2,
};
